import { platforms } from '@sonasi/shared-types';

/**
 * Which platforms an app runs on, as the design's chips.
 *
 * Written once because two screens ask the same question — the editor and the
 * dialog that creates an app — and a second copy is a second place for the list
 * of platforms to go stale.
 *
 * Real buttons carrying `aria-pressed`, not the design's
 * `<span style="cursor: pointer">`: a chip that decides what an app runs on has
 * to be reachable by keyboard and has to say whether it is on.
 */

/**
 * The platforms the API accepts, by their GraphQL enum name.
 *
 * A contract rather than a picture: offering one the server does not know would
 * let somebody pick something every save then rejects.
 *
 * ⚠️ **Derived, not listed.** This was a hand-written copy of the platform
 * list, and it sat outside every check — a platform could be added to the API,
 * the library and both label maps, all of it green, and this picker would still
 * offer three. Reading the list leaves nothing to forget.
 *
 * `.toUpperCase()` is the shape the API speaks: GraphQL serialises an enum by
 * its **member name**, and `platform.spec.ts` in the API pins the name as the
 * upper case of the value.
 */
const PLATFORM_OPTIONS = platforms.map((platform) => ({
  value: platform.toUpperCase(),
  label: platform,
}));

export function PlatformChips({
  selected,
  onToggle,
}: {
  selected: string[];
  onToggle: (platforms: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {PLATFORM_OPTIONS.map((platform) => {
        const on = selected.includes(platform.value);
        return (
          <button
            key={platform.value}
            type="button"
            aria-pressed={on}
            onClick={() =>
              onToggle(
                on
                  ? selected.filter((one) => one !== platform.value)
                  : [...selected, platform.value],
              )
            }
            className="focus-ring inline-flex cursor-pointer items-center gap-1.5 rounded-[7px] border px-2.5 py-[5px] text-xs"
            style={{
              background: on ? 'var(--accent-soft)' : 'transparent',
              borderColor: on ? 'var(--accent)' : 'var(--line2)',
              color: on ? 'var(--accent)' : 'var(--muted)',
            }}
          >
            <span aria-hidden="true" className="font-mono text-[10px]">
              {on ? '✓' : '+'}
            </span>
            {platform.label}
          </button>
        );
      })}
    </div>
  );
}
